import { Tag, Flex, Text, Link, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { AddressNFT } from 'types/api/address';

import { route } from 'nextjs-routes';

import getCurrencyValue from 'lib/getCurrencyValue';
import { useI18n } from 'lib/i18n/context';
import { getTokenTypeName } from 'lib/token/tokenTypes';
import Skeleton from 'ui/shared/chakra/Skeleton';
import NftEntity from 'ui/shared/entities/nft/NftEntity';
import TokenEntity from 'ui/shared/entities/token/TokenEntity';
import NftMedia from 'ui/shared/nft/NftMedia';

import { ADDRESS_TOKEN_KEYS } from '../i18n';
import NFTItemContainer from './NFTItemContainer';

type Props = AddressNFT & { isLoading: boolean; withTokenLink?: boolean };

const NFTItem = ({ token, value, isLoading, withTokenLink, ...tokenInstance }: Props) => {
  const { t } = useI18n();
  const badgeBg = useColorModeValue('rgba(255, 255, 255, 0.92)', 'rgba(17, 17, 17, 0.92)');
  const badgeBorderColor = useColorModeValue('#E2E8F0', 'rgba(255, 255, 255, 0.12)');
  const badgeTextColor = useColorModeValue('#2D3748', '#F7FAFC');
  const valueResult = token.decimals && value ? getCurrencyValue({ value, decimals: token.decimals, accuracy: 2 }).valueStr : value;
  const tokenInstanceLink = tokenInstance.id ?
    route({ pathname: '/token/[hash]/instance/[id]', query: { hash: token.address, id: tokenInstance.id } }) :
    undefined;

  return (
    <NFTItemContainer position="relative">
      <Skeleton isLoaded={ !isLoading }>
        <Tag
          bg={ badgeBg }
          color={ badgeTextColor }
          border="1px solid"
          borderColor={ badgeBorderColor }
          zIndex={ 1 }
          position="absolute"
          top="18px"
          right="18px"
          backdropFilter="blur(8px)"
        >
          { getTokenTypeName(token.type) }
        </Tag>
      </Skeleton>
      <Link href={ isLoading ? undefined : tokenInstanceLink }>
        <NftMedia
          mb="18px"
          data={ tokenInstance }
          isLoading={ isLoading }
          autoplayVideo={ false }
        />
      </Link>
      <Flex justifyContent="space-between" w="100%" flexWrap="wrap">
        <Flex ml={ 1 } overflow="hidden">
          <Text whiteSpace="pre" variant="secondary">{ t(ADDRESS_TOKEN_KEYS.fields.id) } </Text>
          <NftEntity hash={ token.address } id={ tokenInstance.id } isLoading={ isLoading } noIcon/>
        </Flex>
        <Skeleton isLoaded={ !isLoading } overflow="hidden" ml={ 1 }>
          { valueResult && (
            <Flex>
              <Text variant="secondary" whiteSpace="pre">{ t(ADDRESS_TOKEN_KEYS.fields.quantityShort) } </Text>
              <Text overflow="hidden" wordBreak="break-all">{ valueResult }</Text>
            </Flex>
          ) }
        </Skeleton>
      </Flex>
      { withTokenLink && (
        <TokenEntity
          mt={ 2 }
          mb={ 2 }
          token={ token }
          isLoading={ isLoading }
          noCopy
          noSymbol
        />
      ) }
    </NFTItemContainer>
  );
};

export default NFTItem;
